import { FC, useEffect, useMemo, useState } from 'react'
import cn from 'classnames'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import Select from '../base/Select/Select'
import { IcoRemove } from '@/utils/icons'
import { crmApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import { getLeadFieldValue, type LeadFieldsSource } from '../../utils/leadFields'
import { useLeadRefetch } from '../LeadProfileModal/LeadRefetchContext'
import mclasses from '../../styles/modal.module.css'
import classes from './index.module.css'

type RemoveDealLead = LeadFieldsSource & { id: number }

type CurrentFunnelRow = {
  id: number
  lead_funnel_id?: number
  funnel_id: number
  funnel_name: string | null
  deal?: { id: number; name: string } | null
}

const getLeadFunnelRowId = (funnel: CurrentFunnelRow): number => {
  const raw = funnel.lead_funnel_id ?? funnel.id
  const id = Number(raw)
  return Number.isFinite(id) && id > 0 ? id : Number(funnel.id)
}

const getDealOptionLabel = (funnel: CurrentFunnelRow): string => {
  const leadFunnelId = getLeadFunnelRowId(funnel)
  const dealName = funnel.deal?.name?.trim()
  const funnelName = funnel.funnel_name?.trim() || null

  if (dealName) {
    return funnelName ? `${dealName} · ${funnelName}` : dealName
  }

  return funnelName ?? `Сделка #${leadFunnelId}`
}

export type RemoveDealFromLeadSuccessPayload = {
  removedLeadFunnelId: number
}

interface RemoveDealFromLeadModalProps {
  lead?: RemoveDealLead
  open: boolean
  onClose: () => void
  onSuccess?: (payload: RemoveDealFromLeadSuccessPayload) => Promise<void> | void
}

const RemoveDealFromLeadModal: FC<RemoveDealFromLeadModalProps> = ({
  lead,
  open,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const { leadCardSnapshot } = useLeadRefetch()
  const [leadFunnelId, setLeadFunnelId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const currentFunnels = useMemo(() => {
    const list = leadCardSnapshot?.current_funnels
    return Array.isArray(list) ? (list as CurrentFunnelRow[]) : []
  }, [leadCardSnapshot])

  useEffect(() => {
    if (!open) return
    setLeadFunnelId(null)
  }, [open])

  useEffect(() => {
    if (open && !lead) {
      onClose()
    }
  }, [open, lead, onClose])

  const leadName = useMemo(
    () => (lead ? getLeadFieldValue(lead, 'NAME') || '(без фио)' : '-'),
    [lead],
  )

  const dealOptions = useMemo(
    () =>
      currentFunnels.map((funnel) => ({
        value: getLeadFunnelRowId(funnel),
        label: getDealOptionLabel(funnel),
      })),
    [currentFunnels],
  )

  const canDelete = dealOptions.length > 1

  useEffect(() => {
    if (!leadFunnelId) return
    const allowed = new Set(dealOptions.map((option) => option.value))
    if (!allowed.has(leadFunnelId)) {
      setLeadFunnelId(null)
    }
  }, [leadFunnelId, dealOptions])

  const handleConfirm = async () => {
    if (!leadFunnelId) {
      addAlert({
        title: 'Ошибка',
        text: 'Выберите сделку',
        type: 'error',
      })
      return
    }
    if (!canDelete) {
      addAlert({
        title: 'Ошибка',
        text: 'Нельзя удалить последнюю сделку лида',
        type: 'error',
      })
      return
    }

    try {
      setSubmitting(true)
      const response = await crmApi.leads.deleteDeal(leadFunnelId)
      if (isError(response)) throw new Error()

      addAlert({
        title: 'Успешно',
        text: 'Сделка удалена',
        type: 'success',
      })

      if (onSuccess) {
        await onSuccess({ removedLeadFunnelId: leadFunnelId })
      }
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status?: number; data?: { error?: unknown; message?: unknown } }
      }
      const status = axiosError?.response?.status
      const backendError =
        axiosError?.response?.data?.error ??
        axiosError?.response?.data?.message
      const errorText = Array.isArray(backendError)
        ? backendError.join('\n')
        : typeof backendError === 'string'
          ? backendError
          : status === 409
            ? 'Нельзя удалить последнюю сделку лида'
            : 'Не удалось удалить сделку'

      addAlert({
        title: 'Ошибка',
        text: errorText,
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      active={open}
      className={cn(mclasses.modalWrapper, classes.modalWrapper) as any}
      setActive={() => {
        if (submitting) return
        onClose()
      }}
    >
      <div className={cn(mclasses.modalContainer, classes.modalContainer)}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              Выберите сделку для удаления
            </span>
          </div>
          <div
            className={mclasses.closeIcon}
            onClick={() => {
              if (submitting) return
              onClose()
            }}
          >
            <IcoRemove />
          </div>
        </div>

        <div className={cn(mclasses.formWrapper, classes.formWrapper)}>
          <div>
            <p className={mclasses.label}>Выбранный лид</p>
            <div className={classes.leadsList}>
              <p className={classes.leadItem}>{leadName}</p>
            </div>
          </div>

          <p className={classes.warningText}>
            Нельзя удалить последнюю активную сделку лида.
          </p>

          <div>
            <p className={mclasses.label}>Выберите сделку</p>
            <Select
              showSearch
              placeholder={
                canDelete ? 'Выберите сделку' : 'Нет сделок для удаления'
              }
              value={leadFunnelId ?? undefined}
              onChange={(value) => setLeadFunnelId(Number(value))}
              options={dealOptions}
              disabled={submitting || !canDelete}
            />
          </div>
        </div>

        <div className={mclasses.actions}>
          <Button color="gray" onClick={onClose} disabled={submitting}>
            Отменить
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            disabled={!leadFunnelId || !canDelete || submitting}
            isLoading={submitting}
          >
            Удалить
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default RemoveDealFromLeadModal
